/**
 * AuditShareModal
 * "Denetçiye Gönder" — şu anki filtrelere göre belgeleri ZIP olarak
 * paketleyip 30 gün geçerli güvenli bir link üretir ve e-posta gönderir.
 *
 * Modal içinde tarih aralığı, yıl ve ay filtreleri de düzenlenebilir;
 * canlı belge sayısı önizlemesi backend'ten /all-unified üzerinden gelir.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  X, Mail, FileText, Lock, Calendar, Loader2, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TR_MONTHS = [
  '', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const AuditShareModal = ({ open, onClose, filters: parentFilters, documentCount, onShared }) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [ccList, setCcList] = useState([]);
  const [shareLabel, setShareLabel] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Sequence (recurring schedule)
  const [scheduleMode, setScheduleMode] = useState(false);
  const [cadence, setCadence] = useState('monthly');

  // Modal-level overrides for date/year/month
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [year, setYear] = useState('all');
  const [month, setMonth] = useState('all');

  // Live preview count (from /all-unified with merged filters)
  const [previewCount, setPreviewCount] = useState(documentCount ?? 0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const debounceRef = useRef(null);

  // Reset modal local state when (re-)opening
  useEffect(() => {
    if (!open) return;
    setDateFrom(parentFilters?.date_from || '');
    setDateTo(parentFilters?.date_to || '');
    setYear(parentFilters?.year ? String(parentFilters.year) : 'all');
    setMonth(parentFilters?.month ? String(parentFilters.month) : 'all');
    setPreviewCount(documentCount ?? 0);
  }, [open]);

  // Build merged filter object — modal-level date/year/month override parent
  const mergedFilters = useMemo(() => {
    const f = { ...(parentFilters || {}) };
    if (dateFrom) f.date_from = dateFrom; else delete f.date_from;
    if (dateTo) f.date_to = dateTo; else delete f.date_to;
    if (year !== 'all') f.year = parseInt(year, 10); else delete f.year;
    if (month !== 'all') f.month = parseInt(month, 10); else delete f.month;
    return f;
  }, [parentFilters, dateFrom, dateTo, year, month]);

  // Build chips from the merged filters (what will actually be sent)
  const chips = useMemo(() => {
    const out = [];
    const f = mergedFilters;
    if (f.year) out.push(`Yıl: ${f.year}`);
    if (f.month) out.push(`Ay: ${TR_MONTHS[f.month] || f.month}`);
    if (f.date_from) out.push(`≥ ${f.date_from}`);
    if (f.date_to) out.push(`≤ ${f.date_to}`);
    if (f.supplier && f.supplier !== 'all') out.push(`İşletme: ${f.supplier}`);
    if (f.currency && f.currency !== 'all') out.push(`Para: ${f.currency}`);
    if (f.project && f.project !== 'all') out.push(`Proje: ${f.project}`);
    if (f.category && f.category !== 'all') out.push(`Kategori: ${f.category}`);
    if (f.amount_min) out.push(`Min: ${f.amount_min}`);
    if (f.amount_max) out.push(`Max: ${f.amount_max}`);
    if (f.uploaded_by) out.push('Kullanıcı: filtreli');
    return out.length ? out : ['Tüm belgeler'];
  }, [mergedFilters]);

  // Live count preview — re-fetches when filters change
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        Object.entries(mergedFilters).forEach(([k, v]) => {
          if (v !== '' && v !== null && v !== undefined) params.append(k, v);
        });
        const res = await axios.get(`${API}/documents/all-unified?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.success) setPreviewCount(res.data.total || 0);
      } catch (err) {
        console.error('preview count error', err);
      } finally {
        setPreviewLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [open, mergedFilters]);

  if (!open) return null;

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail);

  const handleSend = async () => {
    if (!isEmailValid) {
      toast.error('Geçerli bir e-posta adresi giriniz');
      return;
    }
    if (!scheduleMode && previewCount === 0) {
      toast.error('Paylaşılacak belge bulunamadı — filtreleri kontrol edin');
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      if (scheduleMode) {
        const res = await axios.post(
          `${API}/documents/audit-share-schedules`,
          {
            recipient_email: recipientEmail,
            recipient_name: recipientName,
            cc_emails: ccList,
            share_label: shareLabel,
            note,
            cadence,
            filters: mergedFilters,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.success) {
          setResult({
            __sequence: true,
            schedule: res.data.schedule,
            cadence,
            recipient_email: recipientEmail,
          });
          toast.success('Sekans kuruldu — otomatik gönderimler başladı');
          if (onShared) onShared(res.data);
        } else {
          toast.error('Sekans oluşturulamadı');
        }
      } else {
        const res = await axios.post(
          `${API}/documents/audit-shares`,
          {
            recipient_email: recipientEmail,
            recipient_name: recipientName,
            cc_emails: ccList,
            share_label: shareLabel,
            note,
            filters: mergedFilters,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.success) {
          setResult(res.data);
          toast.success(`${res.data.doc_count} belge paketlendi ve e-posta gönderildi`);
          if (onShared) onShared(res.data);
        } else {
          toast.error('Paylaşım oluşturulamadı');
        }
      }
    } catch (err) {
      console.error('audit-share error:', err);
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSending(false);
    }
  };

  const addCcEmail = () => {
    const e = ccInput.trim();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error('Geçersiz CC e-posta');
      return;
    }
    if (ccList.includes(e)) return;
    setCcList([...ccList, e]);
    setCcInput('');
  };

  const removeCc = (e) => setCcList(ccList.filter((x) => x !== e));

  const close = () => {
    if (sending) return;
    setRecipientEmail('');
    setRecipientName('');
    setCcInput('');
    setCcList([]);
    setShareLabel('');
    setNote('');
    setScheduleMode(false);
    setCadence('monthly');
    setResult(null);
    onClose();
  };

  return (
    <div
      data-testid="audit-share-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={close}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Denetçiye Gönder</h3>
          </div>
          <button
            data-testid="audit-share-close"
            onClick={close}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            disabled={sending}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {result ? (
            result.__sequence ? (
              <div data-testid="audit-share-sequence-success" className="space-y-3 py-2">
                <div className="flex items-center gap-2 text-indigo-600">
                  <CheckCircle2 className="w-6 h-6" />
                  <span className="font-semibold text-lg">Sekans kuruldu</span>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 text-sm space-y-1">
                  <div>Periyot: <b>
                    {result.cadence === 'monthly' ? 'Her ay' :
                     result.cadence === 'quarterly' ? 'Her çeyrek' :
                     'Her yıl'}
                  </b></div>
                  <div>Alıcı: <b>{result.recipient_email}</b></div>
                  <div>İlk gönderim: <b>{new Date(result.schedule.next_run_at).toLocaleString('tr-TR')}</b></div>
                  <div className="text-xs text-gray-600 pt-1">
                    Sekans aktif kaldıkça otomatik paylaşım yapılır. Durdurmak için
                    <b> Sekans Geçmişi</b> sekmesini kullanın.
                  </div>
                </div>
                <button
                  onClick={close}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md font-medium hover:bg-gray-50"
                >
                  Kapat
                </button>
              </div>
            ) : (
            <div data-testid="audit-share-success" className="space-y-3 py-2">
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-6 h-6" />
                <span className="font-semibold text-lg">Paylaşım başarılı</span>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm space-y-1">
                <div><b>{result.doc_count}</b> belge paketlendi ({(result.size_bytes / 1024 / 1024).toFixed(2)} MB)</div>
                {result.skipped_count > 0 && (
                  <div className="text-amber-700">⚠ {result.skipped_count} belge atlandı (erişim hatası)</div>
                )}
                <div>
                  E-posta durumu:{' '}
                  <b className={result.email_status === 'sent' ? 'text-emerald-700' : 'text-red-600'}>
                    {result.email_status === 'sent' ? 'Gönderildi ✉️' : 'Başarısız'}
                  </b>
                </div>
                <div className="text-gray-600 text-xs">
                  Link geçerlilik: {new Date(result.expires_at).toLocaleDateString('tr-TR')}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Denetçi linke tıklayınca aşağıdaki Advancecash paylaşım sayfasında özet ve indirme butonunu görecek.
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded p-2 text-xs break-all font-mono">
                {result.download_url}
              </div>
              <button
                data-testid="audit-share-copy-link"
                onClick={() => {
                  navigator.clipboard.writeText(result.download_url);
                  toast.success('Link kopyalandı');
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
              >
                Linki Kopyala
              </button>
              <button
                onClick={close}
                className="w-full px-4 py-2 border border-gray-300 rounded-md font-medium hover:bg-gray-50"
              >
                Kapat
              </button>
            </div>
            )
          ) : (
            <>
              {/* Live preview banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-sm text-blue-900">
                    {previewLoading ? (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Hesaplanıyor...
                      </span>
                    ) : (
                      <>
                        <span data-testid="audit-preview-count">{previewCount}</span> belge paylaşılacak
                      </>
                    )}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {chips.map((c, i) => (
                    <span key={i} className="text-xs bg-white px-2 py-0.5 rounded border border-blue-200 text-blue-700">
                      {c}
                    </span>
                  ))}
                </div>
              </div>

              {/* Date / Year / Month picker block */}
              <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/50">
                <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  <Calendar className="w-3.5 h-3.5" /> Tarih Filtreleri
                </div>

                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="text-xs text-gray-600 mb-0.5 block">Yıl</label>
                    <select
                      data-testid="audit-modal-year"
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="all">Tüm Yıllar</option>
                      {(() => {
                        const nowY = new Date().getFullYear();
                        const arr = [];
                        for (let y = nowY + 1; y >= nowY - 4; y--) arr.push(y);
                        return arr.map((y) => <option key={y} value={y}>{y}</option>);
                      })()}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-0.5 block">Ay</label>
                    <select
                      data-testid="audit-modal-month"
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="all">Tüm Aylar</option>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                        <option key={m} value={m}>{TR_MONTHS[m]}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-600 mb-0.5 block">Başlangıç Tarihi</label>
                    <input
                      data-testid="audit-modal-date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-0.5 block">Bitiş Tarihi</label>
                    <input
                      data-testid="audit-modal-date-to"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {(dateFrom || dateTo || year !== 'all' || month !== 'all') && (
                  <button
                    data-testid="audit-modal-clear-dates"
                    type="button"
                    onClick={() => { setDateFrom(''); setDateTo(''); setYear('all'); setMonth('all'); }}
                    className="mt-2 text-xs text-red-600 hover:text-red-700 font-medium"
                  >
                    Tarih filtrelerini temizle
                  </button>
                )}
              </div>

              {/* Recipient */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Denetçi E-postası <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid="audit-share-email"
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="denetci@firma.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Denetçi Adı (opsiyonel)
                </label>
                <input
                  data-testid="audit-share-name"
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Sn. Ahmet Yılmaz"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* CC e-postaları */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CC (opsiyonel) — başka kişilere de gönderilsin
                </label>
                <div className="flex gap-2">
                  <input
                    data-testid="audit-share-cc-input"
                    type="email"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCcEmail(); } }}
                    placeholder="mali@firma.com"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    data-testid="audit-share-cc-add"
                    type="button"
                    onClick={addCcEmail}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium"
                  >
                    Ekle
                  </button>
                </div>
                {ccList.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {ccList.map((e) => (
                      <span key={e} data-testid={`audit-share-cc-${e}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs border border-indigo-200">
                        {e}
                        <button onClick={() => removeCc(e)} className="hover:text-indigo-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paylaşım Başlığı (opsiyonel)
                </label>
                <input
                  data-testid="audit-share-label"
                  type="text"
                  value={shareLabel}
                  onChange={(e) => setShareLabel(e.target.value)}
                  placeholder="örn. 2026 Haziran Denetimi"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Not (opsiyonel)
                </label>
                <textarea
                  data-testid="audit-share-note"
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Denetçiye iletmek istediğiniz bilgi..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                />
              </div>

              <div className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 rounded p-2 border border-gray-200">
                <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  Link <b>30 gün</b> geçerlidir ve uzun rastgele bir token içerir.
                  ZIP içeriğinde: belge dosyaları, <b>belgeler_ozet.xlsx</b> ve
                  <b> KAPAK.pdf</b> bulunur.
                </span>
              </div>

              {/* Sekans (recurring) section */}
              <div className="rounded-lg border-2 border-dashed border-indigo-200 bg-indigo-50/30 p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="audit-share-schedule-toggle"
                    type="checkbox"
                    checked={scheduleMode}
                    onChange={(e) => setScheduleMode(e.target.checked)}
                    className="w-4 h-4 accent-indigo-600"
                  />
                  <span className="font-medium text-sm text-gray-900">🔁 Sekans Kur (otomatik tekrar)</span>
                </label>
                {scheduleMode && (
                  <div data-testid="audit-share-schedule-panel" className="mt-3 space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { v: 'monthly',   label: 'Her Ay',     desc: 'Ayın 1\'i' },
                        { v: 'quarterly', label: 'Her Çeyrek', desc: 'Q1/Q2/Q3/Q4' },
                        { v: 'yearly',    label: 'Her Yıl',    desc: '1 Ocak' },
                      ].map((opt) => (
                        <button
                          key={opt.v}
                          data-testid={`audit-share-cadence-${opt.v}`}
                          type="button"
                          onClick={() => setCadence(opt.v)}
                          className={`text-xs px-2 py-2 rounded-md border transition-colors text-center ${
                            cadence === opt.v
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'
                          }`}
                        >
                          <div className="font-semibold">{opt.label}</div>
                          <div className="text-[10px] opacity-80">{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                    <div className="text-xs text-gray-600 bg-white rounded px-2 py-1.5 border border-gray-200">
                      ℹ️ Sekans aktif olduğu sürece, seçtiğiniz periyotta otomatik
                      paylaşım yapılır. <b>Belge Yönetimi → Sekans Geçmişi</b>
                      bölümünden istediğiniz zaman durdurabilirsiniz.
                    </div>
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={close}
                  disabled={sending}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  İptal
                </button>
                <button
                  data-testid="audit-share-send"
                  onClick={handleSend}
                  disabled={sending || !isEmailValid || (!scheduleMode && previewCount === 0)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Gönderiliyor...
                    </>
                  ) : scheduleMode ? (
                    <>🔁 Sekansı Başlat</>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" /> {previewCount} Belgeyi Gönder
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditShareModal;
